import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { ListingImage } from "@/lib/types"; // Import from consolidated types file
import {
  isPlaceholder,
  markAsPlaceholder,
  DEFAULT_IMAGE,
} from "@/lib/config/cdn";

interface UploadedImagesGridProps {
  images: ListingImage[];
  onSetPrimary: (imageId: number) => Promise<void>;
  onDeleteImage: (imageId: number) => Promise<void>;
}

const UploadedImagesGrid = ({
  images,
  onSetPrimary,
  onDeleteImage,
}: UploadedImagesGridProps) => {
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Uploaded Images</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {images.map((image) => (
          <div key={image.id} className="relative group">
            <img
              src={image.url}
              alt="Property"
              className={`h-24 w-full object-cover rounded-md ${
                image.is_primary ? "ring-2 ring-blue-500" : ""
              }`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                // Only set to placeholder if not already using placeholder and not already tracked
                if (!isPlaceholder(target.src)) {
                  target.onerror = null;
                  markAsPlaceholder(target.src);
                  target.src = DEFAULT_IMAGE;
                }
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2 rounded-md">
              {!image.is_primary && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onSetPrimary(Number(image.id))}
                >
                  Set as Primary
                </Button>
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onDeleteImage(Number(image.id))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {image.is_primary === true && (
              <span className="absolute bottom-0 left-0 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-tr-md">
                Primary
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadedImagesGrid;
